/**
 * General formatting and display utility functions for analytics data
 */

/**
 * Format address object into readable string
 * @param {Object} address - Address object
 * @returns {string} - Formatted address string
 */
export const formatAddress = (address) => {
  if (!address) return 'No address available';
  
  const parts = [];
  if (address.street) parts.push(address.street);
  if (address.city) parts.push(address.city);
  if (address.state) parts.push(address.state);
  if (address.zipCode) parts.push(address.zipCode);
  
  return parts.length > 0 ? parts.join(', ') : 'Address not available';
};

/**
 * Get full address as a single string
 * @param {Object} address - Address object
 * @returns {string} - Full address string
 */
export const getFullAddress = (address) => {
  if (!address) return 'No address';
  
  return `${address.street || ''}, ${address.city || ''}, ${address.state || ''} ${address.zipCode || ''}`.trim().replace(/^,\s*|,\s*$/g, '');
};

/**
 * Format status for better display
 * @param {string} status - Raw status string
 * @returns {string} - Formatted status
 */
export const formatStatusDisplay = (status) => {
  const statusMap = {
    'completed': 'Completed',
    'archived': 'Archived',
    'pending': 'Pending',
    'pending_review': 'Pending Review',
    'in_progress': 'In Progress',
    'needs_attention': 'Needs Attention'
  };

  return statusMap[status?.toLowerCase()] || status || 'Unknown';
};

/**
 * Format patient name from screening data
 * @param {Object} screening - Screening data object
 * @returns {string} - Formatted patient name
 */
export const formatPatientName = (screening) => {
  if (!screening) return 'Unknown Patient';
  
  const parts = [];
  if (screening.firstName) parts.push(screening.firstName);
  if (screening.lastName) parts.push(screening.lastName);
  
  if (parts.length > 0) {
    return parts.join(' ');
  }
  
  // Fallback to other possible name fields
  if (screening.name) return screening.name;
  if (screening.patientId) return `Patient ${screening.patientId}`;
  if (screening.id) return `Patient ${screening.id}`;
  
  return 'Unknown Patient';
};

/**
 * Get formatted display value for any data type
 * @param {any} value - Value to format
 * @param {string} type - Type of formatting ('event', 'location', 'date', 'default')
 * @returns {string} - Formatted display value
 */
export const getDisplayValue = (value, type = 'default') => {
  if (value === null || value === undefined) {
    return 'Not available';
  }

  if (typeof value === 'object') {
    switch (type) {
      case 'event':
        return value.title || value.name || 'Unknown Event';
      case 'location':
        if (value.name) {
          return value.address ? `${value.name} - ${value.address}` : value.name;
        }
        return formatAddress(value) || 'Unknown Location';
      case 'date':
        return value instanceof Date ? value.toLocaleDateString() : 
               (value.toString ? new Date(value.toString()).toLocaleDateString() : 'Invalid Date');
      default:
        // For objects, try to find a meaningful string representation
        if (value.title) return value.title;
        if (value.name) return value.name;
        if (value.id) return value.id;
        return JSON.stringify(value);
    }
  }

  return value.toString();
};

/**
 * Extract meaningful text from nested objects for display
 * @param {Object} data - Data object
 * @param {string} path - Dot notation path to extract (e.g., 'queueInfo.eventId.title')
 * @param {string} fallback - Fallback value
 * @returns {string} - Extracted value or fallback
 */
export const extractNestedValue = (data, path, fallback = 'Not available') => {
  try {
    const keys = path.split('.');
    let current = data;
    
    for (const key of keys) {
      if (current && typeof current === 'object' && key in current) {
        current = current[key];
      } else {
        return fallback;
      }
    }
    
    return current !== null && current !== undefined ? current : fallback;
  } catch (error) {
    return fallback;
  }
};

/**
 * Determine event status based on dates and data
 * @param {Object} eventData - Event data object
 * @returns {string} - Event status
 */
export const determineEventStatus = (eventData) => {
  if (!eventData.startDate) return 'unknown';

  const now = new Date();
  const startDate = new Date(eventData.startDate);
  const endDate = eventData.endDate ? new Date(eventData.endDate) : null;

  if (endDate && now > endDate) {
    return 'completed';
  } else if (now >= startDate) {
    return 'active';
  } else {
    return 'planned';
  }
};

/**
 * Calculate age from date of birth
 * @param {string} dateOfBirth - Date of birth string
 * @returns {number} - Age in years
 */
export const calculateAge = (dateOfBirth) => {
  try {
    const birthDate = new Date(dateOfBirth);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    return age;
  } catch (error) {
    return 0;
  }
};
